import { useState, useEffect, useCallback } from "react";
import { useMsal, useAccount } from "@azure/msal-react";
import { AuthenticationResult } from "@azure/msal-browser";
import { clearSignInMarker } from "pymble-ui/components";
import { getUserDetails, getUserGroups } from "../services/graphService";

interface UserData {
  displayName?: string;
  [key: string]: unknown;
}

interface GroupEntry {
  id?: string;
  [key: string]: unknown;
}

/**
 * Hook to fetch user data from microsoft graph api
 */
export function useUserDetails() {
  const [userData, setUserData] = useState<UserData>();
  // userGroups state contains the azure ids of the user's groups
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [userDataLoaded, setUserDataLoaded] = useState(false);

  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const getUser = useCallback(async () => {
    if (account) {
      let response: AuthenticationResult | void;
      try {
        response = await instance.acquireTokenSilent({
          scopes: ["User.Read"],
          account: account,
        });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        console.error("Could not get token silently.  Try interactive.");
        response = await instance.acquireTokenRedirect({
          scopes: ["User.Read"],
          account: account,
        });
      }

      if (response && response.accessToken) {
        const user = await getUserDetails(response.accessToken);
        setUserData(user);

        // Get users current groups and create an array of the group ids
        const groupsResponse = await getUserGroups(response.accessToken);
        const groups = groupsResponse.value;
        setUserGroups(groups.map((g: GroupEntry) => g?.id ?? ""));
      }
    }
    setUserDataLoaded(true);
  }, [account, instance]);

  // Hook to load user data when we have an msal instance and account
  useEffect(() => {
    getUser();
  }, [getUser]);

  const logout = useCallback(async () => {
    clearSignInMarker(instance.getConfiguration().auth.clientId);
    await instance.logout();
  }, [instance]);

  return {
    userData,
    userGroups,
    userDataLoaded,
    instance,
    account,
    accounts,
    inProgress,
    logout,
  };
}
